/* eslint-disable react-refresh/only-export-components -- card helpers
   (extractStringProperty, getEntityName) are co-located with the components
   that use them to keep the surface a single import for callers */
/**
 * Shared building blocks for pipeline card content.
 *
 * Extracted from DealCardContent so that GenericCardContent and any
 * future entity-specific card composition can reuse the title row,
 * association line, and footer (owner + last updated) without
 * duplicating styling or behaviour. Visual output is byte-equivalent
 * to the original DealCardContent layout — entity-specific cards only
 * need to provide the middle (value-row) section.
 */

import { Clock } from '@untitledui/icons';
import type { ReactNode } from 'react';
import { memo, useCallback, useEffect, useState } from 'react';

import { Avatar } from '@/lib/base/avatar/avatar';
import {
  createFaviconLoadGuard,
  extractDomain,
  getAvatarInitials,
  getFaviconUrl,
} from '@/lib/crm/utils/avatar';
import { formatRelativeTime } from '@/lib/utils/timeFormatting';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import type { AssociationType } from '../../core/SchemaClient';
import { CardOwnerPicker } from './CardOwnerPicker';

// ---------------------------------------------------------------------------
// Property helpers — co-located with the components that use them so each
// card variant doesn't have to redefine its own version.
// ---------------------------------------------------------------------------

/**
 * Extract a string-typed property value from a CRM object, returning
 * `undefined` for missing, empty, or non-string values.
 */
export function extractStringProperty(
  obj: CrmObject | undefined,
  propertyName: string
): string | undefined {
  if (!obj) {
    return undefined;
  }
  const value = getPropertyValue<unknown>(obj.properties?.[propertyName]);
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  return typeof value === 'string' ? value : undefined;
}

/**
 * Resolve the display label for a CRM object using the universal name
 * lookup: `ahoy_display_name` first (provided by every entity_kind base),
 * then `ahoy_name` (set on system entities like ``ahoy_deal``), then a
 * caller-provided fallback.
 */
export function getEntityName(
  obj: CrmObject | undefined,
  fallback = '–'
): string {
  if (!obj) {
    return fallback;
  }
  const displayName = getPropertyValue<string>(
    obj.properties?.ahoy_display_name
  )?.trim();
  if (displayName) {
    return displayName;
  }
  const name = getPropertyValue<string>(obj.properties?.ahoy_name)?.trim();
  if (name) {
    return name;
  }
  return fallback;
}

// ---------------------------------------------------------------------------
// CompanyLine — favicon + name. Used as the primary association sub-line
// on a card when a company association is present.
// ---------------------------------------------------------------------------

const CompanyLine = memo(function CompanyLine({
  company,
}: {
  company: CrmObject;
}) {
  const companyName = getEntityName(company);

  const domainRaw = extractStringProperty(company, 'ahoy_domain');
  const domain = domainRaw ? extractDomain(domainRaw) : undefined;
  const avatarUrl = domain ? getFaviconUrl(domain, 64) : undefined;

  const [isFaviconFailed, setIsFaviconFailed] = useState(false);

  useEffect(() => {
    setIsFaviconFailed(false);
  }, [avatarUrl]);

  const handleFaviconLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      createFaviconLoadGuard(() => setIsFaviconFailed(true))(e);
    },
    []
  );

  const showFavicon = avatarUrl && !isFaviconFailed;

  return (
    <div className='flex items-center gap-1.5 mt-0.5'>
      <Avatar
        size='xxs'
        src={showFavicon ? avatarUrl : undefined}
        initials={getAvatarInitials(companyName)}
        onLoad={handleFaviconLoad}
      />
      <span className='text-sm font-semibold text-[var(--color-text-secondary)] truncate'>
        {companyName}
      </span>
    </div>
  );
});

// ---------------------------------------------------------------------------
// ContactLine — initials + name. Fallback association sub-line when no
// company is present.
// ---------------------------------------------------------------------------

const ContactLine = memo(function ContactLine({
  contact,
}: {
  contact: CrmObject;
}) {
  const contactName = getEntityName(contact);

  return (
    <div className='flex items-center gap-1.5 mt-0.5'>
      <Avatar size='xxs' initials={getAvatarInitials(contactName)} />
      <span className='text-sm font-semibold text-[var(--color-text-secondary)] truncate'>
        {contactName}
      </span>
    </div>
  );
});

// ---------------------------------------------------------------------------
// CardTitleRow — title + optional primary association sub-line.
// Renders nothing extra when neither association is present.
// ---------------------------------------------------------------------------

export interface CardTitleRowProps {
  title: string;
  /** Primary association — rendered as a favicon + name line. */
  company?: CrmObject;
  /** Fallback association — used when no company is present. */
  contact?: CrmObject;
}

export const CardTitleRow = memo(function CardTitleRow({
  title,
  company,
  contact,
}: CardTitleRowProps) {
  return (
    <div className='px-3'>
      <span className='font-semibold text-sm text-[var(--color-text-primary)] line-clamp-2'>
        {title}
      </span>
      {company && <CompanyLine company={company} />}
      {!company && contact && <ContactLine contact={contact} />}
    </div>
  );
});

// ---------------------------------------------------------------------------
// LegacyOwnerFallback — read-only owner display for callers that do not pass
// picker context (DealCardContent, GenericCardContent, NoteCardContent).
// No navigation — the picker owns click-to-view for the configurable path.
// ---------------------------------------------------------------------------

const LegacyOwnerFallback = memo(function LegacyOwnerFallback({
  owner,
}: {
  owner: CrmObject;
}) {
  const ownerName =
    getPropertyValue<string>(owner.properties?.ahoy_display_name) || undefined;
  const ownerAvatarUrl = extractStringProperty(owner, 'ahoy_avatar_url');

  if (!ownerName) {
    return <span />;
  }

  return (
    <div className='flex items-center gap-1.5 min-w-0'>
      <Avatar
        size='xxs'
        src={ownerAvatarUrl}
        initials={getAvatarInitials(ownerName)}
      />
      <span className='text-xs text-[var(--color-text-secondary)] truncate'>
        {ownerName}
      </span>
    </div>
  );
});

// ---------------------------------------------------------------------------
// CardLastUpdated — clock icon + relative-time string. Renders an em-dash
// when no timestamp is available so the row never collapses to nothing.
// ---------------------------------------------------------------------------

const CardLastUpdated = memo(function CardLastUpdated({
  updatedAt,
}: {
  updatedAt: string | null;
}) {
  return (
    <div className='flex items-center gap-1 text-xs text-[var(--color-text-tertiary)] flex-shrink-0'>
      <Clock className='size-3 flex-shrink-0' />
      <span>
        Last updated {updatedAt ? formatRelativeTime(updatedAt) : '–'}
      </span>
    </div>
  );
});

// ---------------------------------------------------------------------------
// CardFooter — owner (left) + last-updated (right) inside a top-bordered row.
// Owner side renders as an empty placeholder when no owner is present so
// the last-updated stays right-aligned.
// ---------------------------------------------------------------------------

export interface CardFooterProps {
  owner?: CrmObject;
  /** ISO datetime string for the entity's last-updated time. */
  updatedAt: string | null;
  /**
   * When present together with `ownerAssociationTypeName`, renders
   * `CardOwnerPicker` instead of the read-only fallback. Required for the
   * configurable-card path; omit for legacy card variants (Deal, Generic, Note).
   */
  item?: CrmObject;
  /** Owner association type name, e.g. `'ahoy_deal_to_owner'`. */
  ownerAssociationTypeName?: string;
  /** Schema for the owner association — drives the picker's permission gate. */
  ownerAssociationType?: AssociationType;
}

function resolveOwnerSlot(
  item: CrmObject | undefined,
  owner: CrmObject | undefined,
  ownerAssociationTypeName: string | undefined,
  ownerAssociationType: AssociationType | undefined
) {
  if (item !== undefined && ownerAssociationTypeName !== undefined) {
    return (
      <CardOwnerPicker
        item={item}
        owner={owner}
        associationTypeName={ownerAssociationTypeName}
        associationType={ownerAssociationType}
      />
    );
  }
  return owner ? <LegacyOwnerFallback owner={owner} /> : <span />;
}

export const CardFooter = memo(function CardFooter({
  owner,
  updatedAt,
  item,
  ownerAssociationTypeName,
  ownerAssociationType,
}: CardFooterProps) {
  return (
    <div className='flex items-center justify-between gap-2 mt-3 pt-2 pb-1 px-3 border-t border-[var(--color-border-secondary)] dark:border-white/15'>
      {resolveOwnerSlot(
        item,
        owner,
        ownerAssociationTypeName,
        ownerAssociationType
      )}
      <CardLastUpdated updatedAt={updatedAt} />
    </div>
  );
});

// ---------------------------------------------------------------------------
// CardShell — the standard card layout (title row + custom middle + footer).
// Optional helper that callers can use when they want the full layout
// without composing the three pieces themselves. Card variants that need
// finer control can compose CardTitleRow + CardFooter directly.
// ---------------------------------------------------------------------------

export interface CardShellProps {
  title: string;
  company?: CrmObject;
  contact?: CrmObject;
  /** Middle section — entity-specific value row(s). */
  children?: ReactNode;
  owner?: CrmObject;
  updatedAt: string | null;
}

export const CardShell = memo(function CardShell({
  title,
  company,
  contact,
  children,
  owner,
  updatedAt,
}: CardShellProps) {
  return (
    <div>
      <CardTitleRow title={title} company={company} contact={contact} />
      {children}
      <CardFooter owner={owner} updatedAt={updatedAt} />
    </div>
  );
});
